import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Gift, Users, Search, Send, X, ArrowLeft, ShieldCheck, Trash2, Plus,
} from "lucide-react";

/**
 * /dashboard/diploks/organiser/grants
 * ----------------------------------------------------------------------
 * Organisation admins can grant Diploks from the org wallet to any user
 * (delegate / chair / faculty / volunteer / judge / parent of registered
 * minor).  Supports single + bulk grants, event-scoped grants, expiring
 * grants and cancellation (refunds remaining amount to org wallet).
 */
export default function OrganiserGrantsPage() {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState([]);  // recipient user ids
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const searchTimerRef = useRef(null);
  const initialLoadRef = useRef(false);

  const loadGrants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/diploks/organiser/grants?limit=100");
      setGrants(r.data?.grants || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecipients = useCallback(async (q = "") => {
    try {
      const r = await api.get(`/diploks/organiser/recipients/search?q=${encodeURIComponent(q)}&limit=40`);
      setRecipients(r.data?.recipients || []);
    } catch (e) {
      // Silent — search runs as user types.
    }
  }, []);

  useEffect(() => {
    loadGrants();
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      void loadRecipients("");
    }
  }, [loadGrants, loadRecipients]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      void loadRecipients(v);
    }, 300);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void loadRecipients(search);
    }
  };

  const toggleSelect = (uid) => {
    setSelected((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  const totalCost = useMemo(
    () => Number(amount || 0) * selected.length,
    [amount, selected]
  );

  const submitGrants = async () => {
    if (!selected.length) {
      toast.error("Pick at least one recipient.");
      return;
    }
    if (amount < 1) {
      toast.error("Amount must be at least 1 Diplok.");
      return;
    }
    setSubmitting(true);
    try {
      if (selected.length === 1) {
        await api.post("/diploks/organiser/grants", {
          recipient_user_id: selected[0],
          amount: Number(amount),
          reason,
        });
        toast.success(`Granted ${amount} Diploks.`);
      } else {
        const r = await api.post("/diploks/organiser/grants/bulk", {
          recipient_user_ids: selected,
          amount_per_recipient: Number(amount),
          reason,
        });
        const ok = r.data?.granted?.length || 0;
        const fail = r.data?.failed?.length || 0;
        toast.success(`Granted ${amount * ok} Diploks across ${ok} users${fail ? ` · ${fail} skipped` : ""}.`);
      }
      setSelected([]);
      setReason("");
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
      await loadGrants();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelGrant = async (id) => {
    if (!window.confirm("Cancel this grant? Unconsumed Diploks return to the organisation wallet.")) return;
    try {
      await api.delete(`/diploks/organiser/grants/${id}?reason=cancelled-from-ui`);
      toast.success("Grant cancelled.");
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
      await loadGrants();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="organiser-grants-page">
        <Link to="/dashboard/diploks/wallet"
              className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to Organisation Diploks
        </Link>

        <div className="mt-3 mb-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Organiser
          </p>
          <h1 className="mt-1 text-3xl font-bold text-[#0A192F] tracking-tight">
            Grant Diploks to your community
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Reward delegates, chairs, faculty and volunteers with Diploks from
            your organisation wallet. Recipients can spend them on AI paper
            reviews, premium certificates and other Diplomexa services.
          </p>
        </div>

        {/* Compose */}
        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5"
               data-testid="grants-compose-panel">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
              Pick recipients
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={search}
                onChange={onSearchChange}
                onKeyDown={onSearchKeyDown}
                onBlur={() => loadRecipients(search)}
                placeholder="Search by name or email…"
                data-testid="grants-recipient-search"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm"
              />
            </div>
            <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-slate-100"
                 data-testid="grants-recipient-list">
              {recipients.length === 0 && (
                <p className="text-xs text-slate-500 py-3">
                  No matching recipients yet. Try a different search term.
                </p>
              )}
              {recipients.map((u) => {
                const isSel = selected.includes(u.id);
                return (
                  <label
                    key={u.id}
                    data-testid={`grants-recipient-row-${u.id}`}
                    className={`flex items-center justify-between py-2 cursor-pointer px-2 rounded ${
                      isSel ? "bg-[#D4AF37]/10" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleSelect(u.id)}
                      />
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{u.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {u.email} · {u.role}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {selected.length > 0 && (
              <p className="mt-3 text-xs text-emerald-700"
                 data-testid="grants-selected-count">
                {selected.length} recipient{selected.length > 1 ? "s" : ""} selected.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
              Grant details
            </p>
            <label className="block text-xs text-slate-600 mb-1">
              Diploks per recipient
            </label>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="grants-amount-input"
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
            />
            <label className="block text-xs text-slate-600 mt-3 mb-1">
              Reason (optional)
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Best Position Paper · UNSC"
              data-testid="grants-reason-input"
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
            />
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-slate-500">Total cost</span>
              <span className="text-[#0A192F] font-bold tabular-nums"
                    data-testid="grants-total-cost">
                {totalCost} Diploks
              </span>
            </div>
            <button
              type="button"
              disabled={submitting || selected.length === 0}
              onClick={submitGrants}
              data-testid="grants-submit-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-3 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              {submitting ? "Granting…" : "Grant Diploks"}
            </button>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed flex items-start gap-1">
              <ShieldCheck className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
              Diploks credited via grant are platform credits — they cannot
              be redeemed, transferred or converted to money.
            </p>
          </div>
        </div>

        {/* Recent grants */}
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="grants-history-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
              Recent grants
            </p>
            <button onClick={loadGrants}
                    className="text-[11px] text-[#0F4C81] hover:underline">
              Refresh
            </button>
          </div>
          {loading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading…</p>
          ) : grants.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500">
              No grants yet. Use the form above to send your first grant.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {grants.map((g) => (
                <div key={g.id} data-testid={`grants-row-${g.id}`}
                     className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">
                      {g.recipient_user_id}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {g.reason || "Organiser-sponsored Diploks"} ·{" "}
                      {new Date(g.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold tabular-nums text-[#0A192F]">
                      {g.amount}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      g.status === "active"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-500"
                    }`}>
                      {g.status}
                    </span>
                    {g.status === "active" && (
                      <button onClick={() => cancelGrant(g.id)}
                              data-testid={`grants-cancel-${g.id}`}
                              className="text-slate-400 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
